import { create } from 'zustand'
import type { User, AuthSession, PendingChallenge } from '@/types/auth'

interface AuthStore {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  pendingChallenge: PendingChallenge | null
  authSession: AuthSession | null
  loading: boolean
  error: string | null

  // Actions
  setUser: (user: User | null) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  setPendingChallenge: (challenge: PendingChallenge | null) => void
  setAuthSession: (session: AuthSession | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  logout: () => void
  clearAuth: () => void
}

/**
 * Hydrate auth snapshot from localStorage on first load.
 * If the refresh/access pair is incomplete, strip orphaned `user_data` so a partial
 * logout (memory cleared but storage left behind) cannot restore identity on refresh.
 */
const getInitialAuthSnapshot = (): {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
} => {
  if (typeof window === 'undefined') {
    return { accessToken: null, refreshToken: null, user: null }
  }
  try {
    const accessToken = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')
    const hasValidPair = !!(accessToken && refreshToken)
    if (!hasValidPair) {
      localStorage.removeItem('user_data')
      return { accessToken: null, refreshToken: null, user: null }
    }
    const userData = localStorage.getItem('user_data')
    const user = userData ? (JSON.parse(userData) as User) : null
    return { accessToken, refreshToken, user }
  } catch (error) {
    console.error('Error reading auth snapshot from localStorage:', error)
    return { accessToken: null, refreshToken: null, user: null }
  }
}

const initialAuth = getInitialAuthSnapshot()

export const useAuthStore = create<AuthStore>((set) => ({
  user: initialAuth.user,
  accessToken: initialAuth.accessToken,
  refreshToken: initialAuth.refreshToken,
  isAuthenticated: !!(initialAuth.accessToken && initialAuth.refreshToken),
  pendingChallenge: null,
  authSession: null,
  loading: true, // Start with loading: true to wait for useAuthInit
  error: null,

  setUser: (user) =>
    set((state) => ({
      user,
      // Session is valid only when both tokens exist (never infer from user alone)
      isAuthenticated: !!(state.accessToken && state.refreshToken),
    })),
  
  setTokens: (accessToken, refreshToken) =>
    set({ accessToken, refreshToken, isAuthenticated: !!(accessToken && refreshToken) }),
  
  setPendingChallenge: (pendingChallenge) =>
    set({ pendingChallenge }),
  
  setAuthSession: (authSession) =>
    set({ authSession }),
  
  setLoading: (loading) =>
    set({ loading }),
  
  setError: (error) =>
    set({ error }),
  
  logout: () =>
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      pendingChallenge: null,
      authSession: null,
      error: null,
    }),
  
  clearAuth: () =>
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      pendingChallenge: null,
      authSession: null,
      loading: false,
      error: null,
    }),
}))

// Safety net: token refresh / GraphQL paths dispatch `auth:logout` after storage cleanup
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    useAuthStore.getState().clearAuth()
  })
}
